import { classnames } from '@bem-react/classnames'
import type { IconComponent } from '@consta/icons/Icon'
import type { ButtonPropView } from '@consta/uikit/Button'
import { cnMixSpace } from '@consta/uikit/MixSpace'
import type { PropsWithChildren, ReactNode } from 'react'
import { forwardRef, useEffect, useImperativeHandle } from 'react'

import { useBooleanFlag, useLatest } from '../../../../hooks'
import { useHeaderContext } from '../context'
import { useHeaderBlockContext } from '../HeaderBlocks'
import { HeaderButton } from '../HeaderButton'
import { HeaderExpandablePopover } from '../HeaderExpandablePopover'

const popoverClassName = cnMixSpace({ mH: 's' })

type HeaderMenuProps = {
  className?: string
  buttonLabel: string
  buttonIcon: IconComponent
  buttonView: ButtonPropView
  popoverTitle: string
  isButtonVisible?: boolean
  initialOpen?: boolean
  maxPopoverHeight?: number
  headerSlot?: ReactNode
  onOpenStateChange?: (newState: boolean) => void
  onToggleExpanded?: (isExpanded: boolean) => void
}

export type HeaderMenuApi = {
  toggleMenu: VoidFunction
  closeMenu: VoidFunction
  openMenu: VoidFunction
}

export const HeaderMenu = forwardRef<HeaderMenuApi, PropsWithChildren<HeaderMenuProps>>(
  function HeaderMenu(
    {
      isButtonVisible = true,
      buttonIcon,
      buttonLabel,
      buttonView,
      headerSlot,
      popoverTitle,
      children,
      className,
      maxPopoverHeight,
      initialOpen = false,
      onOpenStateChange,
      onToggleExpanded,
    },
    ref,
  ) {
    const [isPopoverOpened, { toggle: toggleMenu, setFalse: closeMenu, setTrue: openMenu }] =
      useBooleanFlag(initialOpen)
    const headerRef = useHeaderContext()
    const position = useHeaderBlockContext()

    const onOpenStateChangeCallback = useLatest(onOpenStateChange)

    useImperativeHandle(ref, () => ({ closeMenu, openMenu, toggleMenu }), [
      closeMenu,
      openMenu,
      toggleMenu,
    ])

    useEffect(() => {
      onOpenStateChangeCallback.current?.(isPopoverOpened)
    }, [isPopoverOpened])

    return (
      <>
        {!!isButtonVisible && (
          <HeaderButton
            label={buttonLabel}
            view={buttonView}
            icon={buttonIcon}
            onClick={toggleMenu}
          />
        )}
        <HeaderExpandablePopover
          className={classnames(className, popoverClassName)}
          title={popoverTitle}
          isOpen={isPopoverOpened}
          anchorRef={headerRef}
          position={position}
          maxHeight={maxPopoverHeight}
          headerSlot={headerSlot}
          onToggleExpanded={onToggleExpanded}>
          {children}
        </HeaderExpandablePopover>
      </>
    )
  },
)
